<template>
  <WidgetBaseComponent
    v-resize="checkTruncation"
    class="last-actions-widget"
    :loadedData="allDataLoaded && !showSpinner"
  >
    <!-- HEADER -->
    <template #header>
      <div class="header-container">
        <h2>{{ widgetData.title }}</h2>
        <span :class="[openWindowIcon, 'lf-icon__md', 'header-icon']" @click.stop="goToActions"></span>
      </div>
    </template>

    <!-- BODY -->
    <template #body>
      <section class="body-container element-with-scroll">
        <table v-if="widgetData.body.cells.length" class="last-actions-table">
          <thead>
            <tr>
              <th
                v-for="(header, headerIndex) in widgetData.body.headers"
                :key="headerIndex"
                :class="header.toLowerCase()"
              >
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-if="widgetData.body.cells.length !== 0"
              v-for="(row, rowIndex) in typedCells"
              :key="rowIndex"
              @click="openActionInNewWindow(row.idType, row.idAction)"
            >
              <td v-for="(_cell, cellIndex) in widgetData.body.headers" :key="cellIndex">
                <span v-if="cellIndex === 0" :class="[checkActionIcon(row['idType']), 'table-icon']"></span>
                <!-- Subject row -->
                <div v-if="cellIndex === 1">
                  <LexonTooltipComponent
                    v-if="isTruncatedItems[rowIndex]"
                    :message="Object.values(row)[cellIndex + 2]"
                    position="top"
                    isWidthAuto
                  >
                    <template #activator-element>
                      <span :ref="(el) => setRef(el, rowIndex)" class="truncated">{{
                        Object.values(row)[cellIndex + 2]
                      }}</span>
                    </template>
                  </LexonTooltipComponent>
                  <span v-else :ref="(el) => setRef(el, rowIndex)" class="truncated">{{
                    Object.values(row)[cellIndex + 2]
                  }}</span>
                </div>

                <span v-else>{{ Object.values(row)[cellIndex + 2] }}</span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Last actions table empty -->
        <div v-else class="empty-table">{{ emptyTableText }}</div>
      </section>
    </template>
  </WidgetBaseComponent>
</template>

<script setup lang="ts">
import { ref, computed, watch, ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n-composable'
import useRouter from '@/composables/useRouter'
import WidgetBaseComponent from '@/components/widgets/WidgetBaseComponent.vue'
import { Icons } from '@/icons/icons'
import { ContextName } from '@/store/types/storeGlobalTypes'
import { ActionTypes, checkActionIcon } from '@/store/modules/actions/actionsTypes'
import { URLS } from '@/router/routes/urlRoutes'
import useSelectedRegister from '@/composables/useSelectedRegister'
import useStage from '@/composables/useStage'
import useGlobalVisionWidgets, {
  ActionLastActionsWidgetData
} from '@/components/expedients/ExpedientCourts/composables/useGlobalVisionWidgets'
import LexonTooltipComponent from '@/components/forms/fields/LexonTooltip/LexonTooltipComponent.vue'
import useTruncation from '@/composables/useTruncation'

interface ActionRow {
  idType: ActionTypes
  idAction: string
  [key: string]: any
}

// TRANSALATIONS
const { t } = useI18n()

// ROUTER
const router = useRouter()

// CONSTS
const emptyTableText = t('components.widgets.expedient_last_actions.empty_table_text')
const openWindowIcon = Icons.OPEN_WINDOW

const contexts = {
  lastActions: ContextName.EXPEDIENT_LAST_ACTIONS_WIDGET,
  expedientContext: ContextName.EXPEDIENTS
}

// DATA
const showSpinner = ref(true)
const widgetData = ref<ActionLastActionsWidgetData>({
  title: '',
  body: {
    headers: [],
    cells: []
  }
})

// COMPOSABLES
const { selectedRegisterId: selectedExpedientId } = useSelectedRegister({ entityName: contexts.expedientContext })
const { expedientStage } = useStage()
const { fetchExpedientLastActionsWidgetData } = useGlobalVisionWidgets()

// COMPUTED
const typedCells = computed<ActionRow[]>(() => widgetData.value.body.cells as ActionRow[])

const { isTruncatedItems, setRef, checkTruncation } = useTruncation({
  items: typedCells as ComputedRef<ActionRow[]>
})

const allDataLoaded = computed(() => !!widgetData.value.title)

const expedientStageState = computed(() => expedientStage.value)

// METHODS
const fetchLastActionsData = async () => {
  showSpinner.value = true

  widgetData.value = (await fetchExpedientLastActionsWidgetData({
    selectedExpedientId: selectedExpedientId.value,
    filter: {
      source: contexts.lastActions,
      idStage: expedientStageState.value
    }
  })) as ActionLastActionsWidgetData
  showSpinner.value = false
}

const openActionInNewWindow = async (actionType: ActionTypes, selectedActionId: string) => {
  let routeData: any = null

  switch (actionType) {
    case ActionTypes.CALLS:
      routeData = router.resolveRoute({
        name: `${URLS.ACTIONS}-${URLS.CALLS}`,
        params: { selectedRegisterId: selectedActionId }
      })
      break
    case ActionTypes.MEETINGS:
      routeData = router.resolveRoute({
        name: `${URLS.ACTIONS}-${URLS.MEETINGS}`,
        params: { selectedRegisterId: selectedActionId }
      })
      break
    case ActionTypes.PROCEDURES:
      routeData = router.resolveRoute({
        name: `${URLS.ACTIONS}-${URLS.PROCEDURES}`,
        params: { selectedRegisterId: selectedActionId }
      })
      break
    case ActionTypes.OTHERS:
      routeData = router.resolveRoute({
        name: `${URLS.ACTIONS}-${URLS.ACTION_OTHERS}`,
        params: { selectedRegisterId: selectedActionId }
      })
      break
    case ActionTypes.LEXNET:
      routeData = router.resolveRoute({
        name: `${URLS.ACTIONS}-${URLS.ACTION_LEXNET}`,
        params: { selectedRegisterId: selectedActionId }
      })
      break
    case ActionTypes.EMAILS:
      routeData = router.resolveRoute({
        name: `${URLS.ACTIONS}-${URLS.ACTION_EMAILS}`,
        params: { selectedRegisterId: selectedActionId }
      })
      break
    case ActionTypes.INTERNAL_MANAGEMENT:
      routeData = router.resolveRoute({
        name: `${URLS.ACTIONS}-${URLS.ACTION_INTERNAL_MANAGEMENT}`,
        params: { selectedRegisterId: selectedActionId }
      })
      break
    case ActionTypes.TASK:
      routeData = router.resolveRoute({
        name: `${URLS.ACTIONS}-${URLS.ACTION_TASKS}`,
        params: { selectedRegisterId: selectedActionId }
      })
      break
  }

  window.open(routeData.href, '_blank')
}

const goToActions = () => {
  router.goToRoute({
    name: URLS.ACTION_ALL
  })
}

// WATCHERS
watch(
  () => expedientStageState.value,
  () => {
    fetchLastActionsData()
  }
)

// INIT
fetchLastActionsData()
</script>

<style lang="scss" scoped>
.last-actions-widget {
  display: flex;
  height: 366px;
  padding: $spacing-sm;
  flex-direction: column;
  align-items: center;
  border-radius: $cornerRadius-md;
  border: 1px solid $main-300;

  .header-container {
    display: flex;
    width: 100%;
    height: 40px;
    padding: $spacing-xxs 0px;
    justify-content: space-between;
    border-bottom: 1px solid $neutral-grey-400;

    .header-icon {
      color: $main-1000;
      cursor: pointer;
    }
  }

  ::v-deep(main) {
    margin-top: 0 !important;
  }

  .body-container {
    @include flex($flex-direction: column, $justify-content: flex-start);
    @include scroll-styles;
    width: 100%;
    height: 100%;
    padding: $spacing-sm;

    .last-actions-table {
      width: 100%;
      height: 100%;
      font-family: $secondary-font;
      border-collapse: collapse;

      th {
        @include milano-bold-14;
        @include ellipsis;
        color: $main-1000;
        padding: $spacing-xs;

        &.fecha {
          width: 110px;
          max-width: 110px;
        }
      }

      td,
      th {
        height: 38px;
      }

      td {
        @include ellipsis;
        @include arial-regular-12;
        padding: $spacing-xs;
        width: auto;
        max-width: 0;
      }

      tr {
        cursor: pointer;

        &:hover {
          background-color: $main-050;
        }
      }

      .truncated {
        @include ellipsis;
        display: block;
        width: 100%;
        color: $corporate-color;
        font-weight: bold;
      }

      thead {
        text-align: left;
        border-bottom: 2px solid $main-1000;

        tr:hover {
          background-color: transparent;
        }

        th {
          padding-bottom: 4px;
          font-size: 14px;
        }
      }

      tbody {
        td {
          padding: 12.5px $spacing-xs;
          border-bottom: 1px solid $main-200;
        }
      }

      .table-icon {
        font-size: 16px;
        margin-right: $spacing-xxs;
      }
    }

    .empty-table {
      @include milano-regular-16;
      display: flex;
      width: 100%;
      padding: 14px $spacing-xxs 28px $spacing-xxs;
      justify-content: center;
      align-items: center;
      border-bottom: 1px solid $main-200;
      color: $neutral-grey-700;
      text-align: center;
    }
  }
}
</style>
